/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { TDictionary } from "../../../util/types";
import { Product, StockAvailabilityType } from "../../products/storage";
import { StoreInfo } from "../../stores/storage/stores.types";

export interface CartState {
  // Indexed by [accountNumber]
  carts: TDictionary<CartObject>;
  // Indexed by [offlineCartId]
  order: TDictionary<CartOrder>;
  // Indexed by [accountNumber][offlineCartId]
  completeOrders: TDictionary<TDictionary<CartOrder>>;
}

export interface SubmitCartPayload {
  accountNumber: string;
  storeLocationId: string;
  notes: string;
  internalNotes: string;
  customerReference: string;
  email: string;
  phone: string;
  items: CartItemPayload[];
}

export interface CheckoutItemPayload {
  lineId: string;
  productId: number;
  quantity: number;
  editedPriceExcGst?: number;
  lastEditedReasonCode?: string | null;
  lastEditedReasonNote?: string | null;
}

export interface CheckoutPayloadV2 {
  storeLocationId: string;
  notes: string;
  internalNotes: string;
  customerReference: string;
  email: string;
  phone: string;
  items: CheckoutItemPayload[];
}

export interface DeliveryAddress {
  name?: string;
  streetAddress?: string;
  city?: string;
  postcode?: string;
  recordId?: number;
}

export interface CheckoutPayloadV3 {
  deliveryMode: string;
  deliveryAddress?: DeliveryAddress;
  deliveryInstructions?: string;
  dairySupplyNumber?: string;
  isUrgent?: boolean;
  customerName?: string;

  storeLocationId?: string;
  notes: string;
  internalNotes: string;
  customerReference: string;
  email: string;
  phone: string;
  items: CheckoutItemPayload[];
}

export interface SyncCartPayload {
  accountNumber: string;
  branchId: string | undefined;
  cartId: string | null;
  items: SyncItem[];
}

export interface SyncCartResponse {
  accountNumber: string;
  cartId: string;
  items: SyncedItem[];
  totalPrice: number;
  totalAmountIncGst: number;
  gstAmount: number;
  discountIncGst: number;
}

export interface SyncItem {
  productId: number;
  quantity: number;
  lineId: string | null;
  editedPriceExcGst?: number | null;
  lastEditedReasonCode?: string | null;
  lastEditedReasonNote?: string | null;
}

type WithDiscount = SyncItem & ItemDiscounts;

export interface SyncedItem extends WithDiscount {
  basePrice: number;
  basePriceExcGst: number;
  discountedPriceExcGst: number;
  discountedPriceIncGst: number;
  originalPriceExcGst?: number;
  editedPriceExcGst?: number;
}

export interface ProductItemPrices {
  basePriceIncGst: number;
  basePriceExcGst: number;
  discountedPriceExcGst: number;
  discountedPriceIncGst: number;
  originalPriceExcGst?: number;
  editedPriceExcGst?: number;
}
export interface SubmitCartApiResponse {
  confirmationId: string;
}

export interface CartItemPayload {
  productId: number;
  quantity: number;
  lineId: string | null;
  unitPrice: number;
}

export interface CartOrder {
  status: "idle" | "pending" | "fulfilled" | "rejected";
  cart: CartObject;
  error?: CartAPIError;
  erroMessage?: string;
  confirmationId?: string;
  store?: StoreInfo;
}

type ProductWithDiscount = Product & ItemDiscounts;
export interface CartItem extends ProductWithDiscount {
  quantity: number;
  lineId?: string | null;
  stockAvailabilities?: StockAvailabilityType[];
}

export interface QuoteCartItem extends CartItem {
  itemId: string;
}

export enum CartDeliveryMode {
  PICKUP = "PICKUP",
  DELIVERY = "DELIVERY",
}

export interface DeliveryDetails {
  addressName?: string;
  // Formatted address from components
  address?: string;
  // Individual Address Components
  streetAddress?: string;
  city?: string;
  isNewAddress?: boolean;
  postCode?: string;
  uniqueId?: number;
  saveAddress?: boolean;
  deliveryInstructions?: string;
  dairySupplyNumber?: string;
  urgent?: boolean;
  recordId?: number;
}

export interface CartObject {
  offlineCartId: string;
  onlineCartId: string | null;
  accountNumber: string;
  pickupStore?: StoreInfo;
  deliveryDetails?: DeliveryDetails;
  cartDeliveryMode: CartDeliveryMode;
  customerName?: string;

  notes?: string;
  internalNotes?: string;
  customerReference?: string;
  email?: string;
  phone?: string;
  items: CartItem[];
  createdAt: Date;
  updatedAt: Date;
  lastConfirmationId?: string;
  totalPrice: number;
  totalAmountIncGst: number;
  gstAmount: number;
  discountIncGst: number;
  // cartDeliveryMode: CartDeliveryMode
  syncedAt?: Date;
  isQuantityChanged: boolean;
}
export interface Discount {
  offerId: string;
  offerName: string;
  offerDescription: string;
}
export interface ItemDiscounts {
  discounts?: Discount[];
}

export enum ErrorCode {
  ValidationError = "urn:errors:validation-error",
  InvalidStore = "urn:errors:invalid-store",
  InvalidProduct = "urn:errors:invalid-product",
  PricesMismatch = "urn:errors:prices-mismatch",
  InvalidCart = "urn:errors:invalid-cart",
  OfflineError = "local:errors:user-offline",
  CSU = "urn:errors:csu-api-failed",
}

export interface CartPriceErrorResponse {
  productId: number;
  newPrice?: number;
  oldPrice?: number;
}

export interface CartAPIError {
  priceChanges?: CartPriceErrorResponse[];
  unavailableProducts?: number[];
  status?: number;
  title?: string;
  type?: ErrorCode;
}
